'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { Toaster, toast } from 'sonner';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';

// Create supabase client once outside the component to avoid infinite re-render
const supabase = createClient();

interface DiscoveryDevice {
  id: string;
  ip_address: string;
  mac_address: string;
  hostname: string;
  vendor: string;
  porte_aperte: string[];
  os_rilevato: string;
  stato: 'nuovo' | 'noto' | 'ignorato' | 'approvato';
  prima_vista: string;
  ultima_vista: string;
  device_id: string | null;
  note: string;
}

const statoColors: Record<string, string> = {
  nuovo: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  noto: 'bg-sky-500/20 text-sky-400 border-sky-500/30',
  ignorato: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
  approvato: 'bg-green-500/20 text-green-400 border-green-500/30',
};

const statoLabels: Record<string, string> = {
  nuovo: 'Nuovo',
  noto: 'Noto',
  ignorato: 'Ignorato',
  approvato: 'Approvato',
};

export default function NetworkDiscoveryContent() {
  const [devices, setDevices] = useState<DiscoveryDevice[]>([]);
  const [loading, setLoading] = useState(true);
  const [statoFilter, setStatoFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [scanning, setScanning] = useState(false);
  const [addingToInventory, setAddingToInventory] = useState<string | null>(null);

  const fetchDevices = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.from('network_discovery').select('*').order('ultima_vista', { ascending: false });
      if (error) throw error;
      setDevices(data || []);
    } catch (err: any) {
      toast.error('Errore caricamento discovery: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchDevices(); }, [fetchDevices]);

  const filtered = devices.filter(d => {
    const matchStato = statoFilter === 'all' || d.stato === statoFilter;
    const matchSearch = !search || d.ip_address.includes(search) || d.hostname.toLowerCase().includes(search.toLowerCase()) || d.mac_address.toLowerCase().includes(search.toLowerCase());
    return matchStato && matchSearch;
  });

  const handleUpdateStato = async (id: string, stato: string) => {
    try {
      const { error } = await supabase.from('network_discovery').update({ stato }).eq('id', id);
      if (error) throw error;
      toast.success('Stato aggiornato');
      fetchDevices();
    } catch (err: any) {
      toast.error('Errore: ' + err.message);
    }
  };

  const handleScan = async () => {
    setScanning(true);
    try {
      // Simulate a scan trigger - in production this would call an external agent
      // For now, we mark all 'nuovo' devices as recently scanned and refresh
      await supabase.from('network_discovery').update({ ultima_vista: new Date().toISOString() }).eq('stato', 'nuovo');
      toast.success('Scansione avviata — i risultati verranno aggiornati automaticamente');
      await fetchDevices();
    } catch (err: any) {
      toast.error('Errore avvio scansione: ' + err.message);
    } finally {
      setScanning(false);
    }
  };

  const handleAddToInventory = async (d: DiscoveryDevice) => {
    setAddingToInventory(d.id);
    try {
      // Insert into devices table
      const { error: devErr } = await supabase.from('devices').insert({
        hostname: d.hostname || `device-${d.ip_address}`,
        ip_address: d.ip_address,
        mac_address: d.mac_address || null,
        categoria: 'altro',
        stato: 'online',
        produttore: d.vendor || null,
        os_rilevato: d.os_rilevato || null,
      });
      if (devErr) throw devErr;
      // Mark as approved in discovery
      await supabase.from('network_discovery').update({ stato: 'approvato' }).eq('id', d.id);
      toast.success(`${d.hostname || d.ip_address} aggiunto all'inventario`);
      fetchDevices();
    } catch (err: any) {
      toast.error('Errore: ' + err.message);
    } finally {
      setAddingToInventory(null);
    }
  };

  const counts = {
    nuovo: devices.filter(d => d.stato === 'nuovo').length,
    approvato: devices.filter(d => d.stato === 'approvato').length,
    ignorato: devices.filter(d => d.stato === 'ignorato').length,
    noto: devices.filter(d => d.stato === 'noto').length,
  };

  return (
    <div className="flex flex-col h-full bg-background">
      <Toaster position="bottom-right" theme="dark" richColors />

      <div className="px-6 py-5 border-b border-border bg-surface flex-shrink-0">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <h1 className="text-2xl font-bold text-white">Network Discovery</h1>
            </div>
            <p className="text-sm text-secondary-color mt-0.5">Rilevamento automatico dispositivi connessi alla rete</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={fetchDevices} className="flex items-center gap-2 px-4 py-2 bg-surface-elevated border border-border text-secondary-color rounded-lg text-sm font-semibold hover:text-white transition-all">
              <Icon name="ArrowPathIcon" size={15} />
              Aggiorna
            </button>
            <button
              onClick={handleScan}
              disabled={scanning}
              className="flex items-center gap-2 px-4 py-2 bg-sky-500 text-white rounded-lg text-sm font-semibold hover:bg-sky-400 transition-all disabled:opacity-60"
            >
              <Icon name={scanning ? 'ArrowPathIcon' : 'MagnifyingGlassIcon'} size={15} className={scanning ? 'animate-spin' : ''} />
              {scanning ? 'Scansione...' : 'Avvia Scansione'}
            </button>
          </div>
        </div>

        <div className="mt-3 flex items-start gap-2 bg-surface-elevated border border-border rounded-xl px-4 py-3">
          <Icon name="InformationCircleIcon" size={16} className="text-sky-400 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-secondary-color">
            <span className="font-semibold text-white">Nota:</span> La scansione reale richiede un agente esterno (SNMP/Nmap) installato nella rete locale.
            I dati in tabella provengono da importazioni manuali o da un agente esterno configurato.
          </p>
        </div>

        <div className="grid grid-cols-4 gap-3 mt-4">
          {[
            { label: 'Nuovi', count: counts.nuovo, color: 'text-amber-400', bg: 'bg-amber-500/10 border-amber-500/20' },
            { label: 'Approvati', count: counts.approvato, color: 'text-green-400', bg: 'bg-green-500/10 border-green-500/20' },
            { label: 'Noti', count: counts.noto, color: 'text-sky-400', bg: 'bg-sky-500/10 border-sky-500/20' },
            { label: 'Ignorati', count: counts.ignorato, color: 'text-gray-400', bg: 'bg-gray-500/10 border-gray-500/20' },
          ].map(s => (
            <div key={s.label} className={`rounded-lg border px-4 py-3 ${s.bg}`}>
              <p className={`text-xs font-bold uppercase tracking-wide ${s.color} opacity-70`}>{s.label}</p>
              <p className={`text-2xl font-bold mt-0.5 ${s.color}`}>{s.count}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="px-6 py-3 border-b border-border bg-surface flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-48">
          <Icon name="MagnifyingGlassIcon" size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-color" />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Cerca IP, hostname, MAC..." className="w-full bg-surface-elevated border border-border text-white text-sm rounded-lg pl-9 pr-3 py-2 focus:outline-none focus:border-sky-500/50" />
        </div>
        <div className="relative">
          <select value={statoFilter} onChange={e => setStatoFilter(e.target.value)} className="bg-surface-elevated border border-border text-secondary-color text-sm font-semibold rounded-lg px-3 py-2 focus:outline-none appearance-none pr-7 cursor-pointer">
            <option value="all">Tutti gli stati</option>
            <option value="nuovo">Nuovo</option>
            <option value="approvato">Approvato</option>
            <option value="noto">Noto</option>
            <option value="ignorato">Ignorato</option>
          </select>
          <Icon name="ChevronDownIcon" size={13} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-color pointer-events-none" />
        </div>
        <span className="text-sm text-muted-color font-semibold ml-auto">{filtered.length} dispositivi</span>
      </div>

      <div className="flex-1 overflow-auto scrollbar-thin">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-8 h-8 border-2 border-sky-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 gap-3">
            <Icon name="MagnifyingGlassIcon" size={40} className="text-muted-color" />
            <p className="text-secondary-color font-semibold">Nessun dispositivo trovato</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-surface border-b border-border z-10">
              <tr>
                {['IP Address', 'MAC Address', 'Hostname', 'Vendor', 'OS', 'Porte Aperte', 'Prima Vista', 'Ultima Vista', 'Stato', 'Azioni'].map(h => (
                  <th key={h} className="text-left px-4 py-3 text-xs font-bold text-muted-color uppercase tracking-wider whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filtered.map(d => (
                <tr key={d.id} className="hover:bg-surface-elevated/30 transition-colors">
                  <td className="px-4 py-3 font-mono text-white font-semibold">{d.ip_address}</td>
                  <td className="px-4 py-3 font-mono text-secondary-color text-xs">{d.mac_address || '—'}</td>
                  <td className="px-4 py-3 text-secondary-color">{d.hostname || '—'}</td>
                  <td className="px-4 py-3 text-secondary-color text-xs">{d.vendor || '—'}</td>
                  <td className="px-4 py-3 text-secondary-color text-xs">{d.os_rilevato || '—'}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {(d.porte_aperte || []).slice(0, 3).map(p => (
                        <span key={p} className="px-1.5 py-0.5 bg-surface-elevated border border-border rounded text-xs font-mono text-secondary-color">{p}</span>
                      ))}
                      {(d.porte_aperte || []).length > 3 && <span className="text-xs text-muted-color">+{d.porte_aperte.length - 3}</span>}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-secondary-color text-xs">{new Date(d.prima_vista).toLocaleDateString('it-IT')}</td>
                  <td className="px-4 py-3 text-secondary-color text-xs">{new Date(d.ultima_vista).toLocaleString('it-IT')}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-bold border ${statoColors[d.stato]}`}>
                      <span className="w-1.5 h-1.5 rounded-full bg-current" />
                      {statoLabels[d.stato]}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1 flex-wrap">
                      {d.stato === 'nuovo' && (
                        <button
                          onClick={() => handleAddToInventory(d)}
                          disabled={addingToInventory === d.id}
                          className="text-xs px-2 py-1 bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 rounded-lg transition-colors font-semibold disabled:opacity-50"
                        >
                          {addingToInventory === d.id ? '...' : '+ Inventario'}
                        </button>
                      )}
                      {d.stato !== 'approvato' && (
                        <button onClick={() => handleUpdateStato(d.id, 'approvato')} className="text-xs px-2 py-1 bg-green-500/10 text-green-400 hover:bg-green-500/20 rounded-lg transition-colors font-semibold">Approva</button>
                      )}
                      {d.stato !== 'ignorato' && (
                        <button onClick={() => handleUpdateStato(d.id, 'ignorato')} className="text-xs px-2 py-1 bg-gray-500/10 text-gray-400 hover:bg-gray-500/20 rounded-lg transition-colors font-semibold">Ignora</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
